import React, { useState, useEffect, useCallback } from 'react';
import { Helmet } from 'react-helmet';
import { useNavigate } from 'react-router-dom';
import PageLayout from '../../components/ui/PageLayout';
import { useAuth } from '../../contexts/AuthContext';
import { useToast } from '../../components/ui/Toast';
import { supabase } from '../../lib/supabase';
import { Download, Search, DollarSign, Calendar, CheckCircle, AlertCircle, Clock, Loader, RefreshCw, FileText, BarChart2 } from 'lucide-react';
import PaymentMetrics from './components/PaymentMetrics';

const getMockTransactions = () => [
  { id: 'txn_001', description: 'Professional Plan - Monthly', plan_name: 'Professional', amount: 384, status: 'succeeded', created_at: new Date(Date.now() - 30 * 24 * 60 * 60 * 1000)?.toISOString(), currency: 'usd' },
  { id: 'txn_002', description: 'Professional Plan - Monthly', plan_name: 'Professional', amount: 384, status: 'succeeded', created_at: new Date(Date.now() - 60 * 24 * 60 * 60 * 1000)?.toISOString(), currency: 'usd' },
  { id: 'txn_003', description: 'Basic Plan - Monthly', plan_name: 'Basic', amount: 163, status: 'succeeded', created_at: new Date(Date.now() - 90 * 24 * 60 * 60 * 1000)?.toISOString(), currency: 'usd' },
];

const downloadInvoicePDF = (tx, user) => {
  const formatDate = (d) => d ? new Date(d)?.toLocaleDateString('en-US', { year: 'numeric', month: 'long', day: 'numeric' }) : 'N/A';
  const formatCurrency = (n) => new Intl.NumberFormat('en-US', { style: 'currency', currency: 'USD' })?.format(n || 0);
  const invoiceNumber = `INV-${(tx?.id || 'UNKNOWN')?.substring(0, 8)?.toUpperCase()}`;
  const issueDate = formatDate(tx?.created_at);
  const amount = tx?.amount || 0;
  const statusLabel = (tx?.status === 'succeeded' || tx?.status === 'paid') ? 'PAID' : (tx?.status || 'PENDING')?.toUpperCase();
  const statusColor = (tx?.status === 'succeeded' || tx?.status === 'paid') ? '#16a34a' : '#d97706';
  const planName = tx?.description || tx?.plan_name || 'Subscription Payment';
  const userName = user?.user_metadata?.full_name || user?.email?.split('@')?.[0] || 'Customer';
  const userEmail = user?.email || '';

  const html = `<!DOCTYPE html>
<html>
<head>
  <meta charset="utf-8">
  <title>Invoice ${invoiceNumber} - LeadGen Pro</title>
  <style>
    * { margin: 0; padding: 0; box-sizing: border-box; }
    body { font-family: -apple-system, BlinkMacSystemFont, 'Segoe UI', Arial, sans-serif; color: #1a1a2e; background: #fff; padding: 40px; }
    .invoice-wrapper { max-width: 700px; margin: 0 auto; }
    .header { display: flex; justify-content: space-between; align-items: flex-start; margin-bottom: 40px; padding-bottom: 24px; border-bottom: 2px solid #e5e7eb; }
    .brand h1 { font-size: 28px; font-weight: 800; color: #1d4ed8; letter-spacing: -0.5px; }
    .brand p { font-size: 13px; color: #6b7280; margin-top: 4px; }
    .invoice-meta { text-align: right; }
    .invoice-meta h2 { font-size: 22px; font-weight: 700; color: #111827; }
    .invoice-meta p { font-size: 13px; color: #6b7280; margin-top: 2px; }
    .status-badge { display: inline-block; padding: 4px 12px; border-radius: 20px; font-size: 12px; font-weight: 700; letter-spacing: 0.5px; color: ${statusColor}; background: ${statusColor}18; border: 1px solid ${statusColor}40; margin-top: 8px; }
    .info-grid { display: grid; grid-template-columns: 1fr 1fr; gap: 24px; margin-bottom: 32px; }
    .info-box { background: #f9fafb; border: 1px solid #e5e7eb; border-radius: 10px; padding: 16px; }
    .info-box h4 { font-size: 11px; font-weight: 600; color: #9ca3af; text-transform: uppercase; letter-spacing: 0.8px; margin-bottom: 8px; }
    .info-box p { font-size: 14px; color: #374151; line-height: 1.6; }
    .info-box .name { font-weight: 600; color: #111827; font-size: 15px; }
    table { width: 100%; border-collapse: collapse; margin-bottom: 24px; }
    thead tr { background: #1d4ed8; }
    thead th { padding: 12px 16px; text-align: left; font-size: 12px; font-weight: 600; color: #fff; text-transform: uppercase; letter-spacing: 0.5px; }
    thead th:last-child { text-align: right; }
    tbody tr { border-bottom: 1px solid #f3f4f6; }
    tbody tr:last-child { border-bottom: none; }
    tbody td { padding: 14px 16px; font-size: 14px; color: #374151; }
    tbody td:last-child { text-align: right; font-weight: 600; color: #111827; }
    .totals { display: flex; justify-content: flex-end; margin-bottom: 32px; }
    .totals-box { width: 260px; }
    .totals-row { display: flex; justify-content: space-between; padding: 6px 0; font-size: 14px; color: #6b7280; }
    .totals-row.total { border-top: 2px solid #e5e7eb; margin-top: 8px; padding-top: 12px; font-size: 17px; font-weight: 700; color: #111827; }
    .payment-note { background: #f0fdf4; border: 1px solid #bbf7d0; border-radius: 10px; padding: 14px 18px; margin-bottom: 28px; display: flex; align-items: center; gap: 10px; }
    .payment-note .check { color: #16a34a; font-size: 18px; }
    .payment-note p { font-size: 13px; color: #15803d; }
    .footer { border-top: 1px solid #e5e7eb; padding-top: 20px; display: flex; justify-content: space-between; align-items: center; }
    .footer p { font-size: 12px; color: #9ca3af; }
    @media print { body { padding: 20px; } }
  </style>
</head>
<body>
  <div class="invoice-wrapper">
    <div class="header">
      <div class="brand">
        <h1>LeadGen Pro</h1>
        <p>AI-Powered Lead Generation Platform</p>
        <p>billing@leadgenproai.com</p>
      </div>
      <div class="invoice-meta">
        <h2>INVOICE</h2>
        <p>${invoiceNumber}</p>
        <p>Issued: ${issueDate}</p>
        <div class="status-badge">${statusLabel}</div>
      </div>
    </div>
    <div class="info-grid">
      <div class="info-box">
        <h4>From</h4>
        <p class="name">LeadGen Pro AI</p>
        <p>123 Innovation Drive</p>
        <p>San Francisco, CA 94105</p>
        <p>United States</p>
        <p>billing@leadgenproai.com</p>
      </div>
      <div class="info-box">
        <h4>Bill To</h4>
        <p class="name">${userName}</p>
        <p>${userEmail}</p>
        <p>Transaction ID: ${tx?.id || 'N/A'}</p>
      </div>
    </div>
    <table>
      <thead>
        <tr>
          <th>Description</th>
          <th>Billing Period</th>
          <th>Qty</th>
          <th>Amount</th>
        </tr>
      </thead>
      <tbody>
        <tr>
          <td>${planName}</td>
          <td>${issueDate}</td>
          <td>1</td>
          <td>${formatCurrency(amount)}</td>
        </tr>
      </tbody>
    </table>
    <div class="totals">
      <div class="totals-box">
        <div class="totals-row"><span>Subtotal</span><span>${formatCurrency(amount)}</span></div>
        <div class="totals-row"><span>Tax (0%)</span><span>$0.00</span></div>
        <div class="totals-row total"><span>Total</span><span>${formatCurrency(amount)}</span></div>
      </div>
    </div>
    ${(tx?.status === 'succeeded' || tx?.status === 'paid') ? `
    <div class="payment-note">
      <span class="check">✓</span>
      <p>Payment of ${formatCurrency(amount)} received on ${issueDate}. Thank you for your business!</p>
    </div>` : ''}
    <div class="footer">
      <p>Thank you for choosing LeadGen Pro AI</p>
      <p>Questions? Contact billing@leadgenproai.com</p>
    </div>
  </div>
</body>
</html>`;

  const printWindow = window.open('', '_blank', 'width=800,height=900');
  if (!printWindow) {
    alert('Please allow popups to download invoices.');
    return;
  }
  printWindow?.document?.write(html);
  printWindow?.document?.close();
  printWindow?.focus();
  setTimeout(() => { printWindow?.print(); }, 500);
};

const BillingHistory = () => {
  const { user, isInitialized, loading: authLoading } = useAuth();
  const navigate = useNavigate();
  const toast = useToast();
  const [transactions, setTransactions] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchQuery, setSearchQuery] = useState('');
  const [statusFilter, setStatusFilter] = useState('all');
  const [summary, setSummary] = useState({ totalSpent: 0, successCount: 0, pendingCount: 0, failedCount: 0 });
  const [activeTab, setActiveTab] = useState('transactions');

  const fetchBillingHistory = useCallback(async () => {
    if (!user?.id) {
      setTransactions(getMockTransactions());
      setLoading(false);
      return;
    }
    setLoading(true);
    try {
      if (!supabase) {
        setTransactions(getMockTransactions());
        return;
      }
      const { data, error } = await supabase
        ?.from('payment_transactions')
        ?.select('*')
        ?.eq('user_id', user?.id)
        ?.order('created_at', { ascending: false });

      if (error) {
        console.warn('Billing history fetch error (using mock data):', error?.message);
        setTransactions(getMockTransactions());
      } else {
        const txData = data || [];
        if (txData?.length === 0) {
          setTransactions(getMockTransactions());
        } else {
          setTransactions(txData?.map(tx => ({
            ...tx,
            amount: tx?.amount > 1000 ? Math.round(tx?.amount / 100) : tx?.amount
          })));
        }
      }
    } catch (err) {
      console.error('Error fetching billing history:', err);
      setTransactions(getMockTransactions());
    } finally {
      setLoading(false);
    }
  }, [user?.id]);

  useEffect(() => {
    if (!isInitialized || authLoading) return;
    if (!user) { navigate('/login', { replace: true }); return; }
    fetchBillingHistory();
  }, [user, isInitialized, authLoading, fetchBillingHistory, navigate]);

  useEffect(() => {
    const succeeded = transactions?.filter(t => t?.status === 'succeeded' || t?.status === 'paid');
    const pending = transactions?.filter(t => t?.status === 'pending' || t?.status === 'processing');
    const failed = transactions?.filter(t => t?.status === 'failed' || t?.status === 'canceled');
    setSummary({
      totalSpent: succeeded?.reduce((sum, t) => sum + (t?.amount || 0), 0),
      successCount: succeeded?.length,
      pendingCount: pending?.length,
      failedCount: failed?.length,
    });
  }, [transactions]);

  const filteredTransactions = transactions?.filter(tx => {
    const matchesSearch = !searchQuery ||
      tx?.description?.toLowerCase()?.includes(searchQuery?.toLowerCase()) ||
      tx?.id?.toLowerCase()?.includes(searchQuery?.toLowerCase()) ||
      tx?.plan_name?.toLowerCase()?.includes(searchQuery?.toLowerCase());
    const matchesStatus = statusFilter === 'all' || tx?.status === statusFilter;
    return matchesSearch && matchesStatus;
  });

  const getStatusBadge = (status) => {
    const map = {
      succeeded: { label: 'Paid', class: 'bg-green-100 text-green-700', icon: CheckCircle },
      paid: { label: 'Paid', class: 'bg-green-100 text-green-700', icon: CheckCircle },
      pending: { label: 'Pending', class: 'bg-yellow-100 text-yellow-700', icon: Clock },
      processing: { label: 'Processing', class: 'bg-blue-100 text-blue-700', icon: Loader },
      failed: { label: 'Failed', class: 'bg-red-100 text-red-700', icon: AlertCircle },
      canceled: { label: 'Cancelled', class: 'bg-gray-100 text-gray-700', icon: AlertCircle },
    };
    return map?.[status] || { label: status || 'Unknown', class: 'bg-gray-100 text-gray-700', icon: AlertCircle };
  };

  const handleDownloadInvoice = (tx) => {
    downloadInvoicePDF(tx, user);
    toast?.success(`Invoice ${tx?.id?.substring(0, 8)?.toUpperCase()} downloaded!`, 'Invoice Downloaded ✓');
  };

  const handleExportAll = () => {
    if (filteredTransactions?.length === 0) {
      toast?.warning('No transactions to export.', 'Nothing to Export');
      return;
    }
    const csv = [
      'Invoice ID,Description,Amount,Status,Date',
      ...filteredTransactions?.map(tx => {
        const date = tx?.created_at ? new Date(tx?.created_at)?.toLocaleDateString() : 'N/A';
        return `INV-${(tx?.id || '')?.substring(0, 8)?.toUpperCase()},"${tx?.description || tx?.plan_name || 'Subscription'}",${tx?.amount || 0},${tx?.status || 'unknown'},${date}`;
      })
    ]?.join('\n');
    const blob = new Blob([csv], { type: 'text/csv' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `billing_history_${new Date()?.toISOString()?.split('T')?.[0]}.csv`;
    a?.click();
    URL.revokeObjectURL(url);
    toast?.success(`Exported ${filteredTransactions?.length} transactions.`, 'Export Complete ✓');
  };

  if (authLoading || loading) {
    return (
      <PageLayout>
        <Helmet><title>Billing History - LeadGen Pro</title></Helmet>
        <div className="flex items-center justify-center min-h-[400px]">
          <Loader className="w-8 h-8 animate-spin text-primary" />
        </div>
      </PageLayout>
    );
  }

  return (
    <PageLayout>
      <Helmet>
        <title>Billing History - LeadGen Pro</title>
        <meta name="description" content="View your billing history and download invoices" />
      </Helmet>
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center justify-between mb-8 gap-4">
        <div>
          <h1 className="text-3xl font-bold text-foreground">Billing History</h1>
          <p className="text-muted-foreground mt-1">View transactions and download invoices</p>
        </div>
        <div className="flex items-center gap-3">
          <button
            onClick={fetchBillingHistory}
            className="flex items-center gap-2 px-4 py-2 border border-border rounded-xl text-sm font-medium text-foreground hover:bg-muted transition-colors"
          >
            <RefreshCw className="w-4 h-4" />
            Refresh
          </button>
          <button
            onClick={handleExportAll}
            className="flex items-center gap-2 px-4 py-2 bg-primary hover:bg-primary/90 text-primary-foreground rounded-xl text-sm font-semibold transition-colors"
          >
            <Download className="w-4 h-4" />
            Export CSV
          </button>
        </div>
      </div>

      {/* Tab Navigation */}
      <div className="flex gap-1 bg-muted/50 rounded-xl p-1 mb-6 w-fit">
        <button
          onClick={() => setActiveTab('transactions')}
          className={`flex items-center gap-2 px-4 py-2 rounded-lg text-sm font-medium transition-colors ${
            activeTab === 'transactions' ? 'bg-card text-foreground shadow-sm border border-border' : 'text-muted-foreground hover:text-foreground'
          }`}
        >
          <FileText className="w-4 h-4" />
          Transactions
        </button>
        <button
          onClick={() => setActiveTab('metrics')}
          className={`flex items-center gap-2 px-4 py-2 rounded-lg text-sm font-medium transition-colors ${
            activeTab === 'metrics' ? 'bg-card text-foreground shadow-sm border border-border' : 'text-muted-foreground hover:text-foreground'
          }`}
        >
          <BarChart2 className="w-4 h-4" />
          Payment Metrics
        </button>
      </div>

      {activeTab === 'metrics' ? (
        <PaymentMetrics />
      ) : (
        <>
      {/* Summary Cards */}
      <div className="grid grid-cols-2 sm:grid-cols-4 gap-4 mb-8">
        {[
          { label: 'Total Spent', value: `$${summary?.totalSpent?.toLocaleString()}`, icon: DollarSign, color: 'text-green-600', bg: 'bg-green-50' },
          { label: 'Successful', value: summary?.successCount, icon: CheckCircle, color: 'text-blue-600', bg: 'bg-blue-50' },
          { label: 'Pending', value: summary?.pendingCount, icon: Clock, color: 'text-yellow-600', bg: 'bg-yellow-50' },
          { label: 'Failed', value: summary?.failedCount, icon: AlertCircle, color: 'text-red-600', bg: 'bg-red-50' },
        ]?.map((item, i) => {
          const ItemIcon = item?.icon;
          return (
            <div key={i} className="bg-card border border-border rounded-xl p-4 shadow-elevation-1">
              <div className={`w-9 h-9 ${item?.bg} rounded-lg flex items-center justify-center mb-3`}>
                <ItemIcon className={`w-5 h-5 ${item?.color}`} />
              </div>
              <p className="text-2xl font-bold text-foreground">{item?.value}</p>
              <p className="text-xs text-muted-foreground mt-0.5">{item?.label}</p>
            </div>
          );
        })}
      </div>
      {/* Filters */}
      <div className="flex flex-col sm:flex-row gap-3 mb-6">
        <div className="relative flex-1">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
          <input
            type="text"
            placeholder="Search transactions..."
            value={searchQuery}
            onChange={e => setSearchQuery(e?.target?.value)}
            className="w-full pl-10 pr-4 py-2.5 border border-border rounded-xl bg-background text-foreground text-sm focus:outline-none focus:ring-2 focus:ring-primary/40"
          />
        </div>
        <select
          value={statusFilter}
          onChange={e => setStatusFilter(e?.target?.value)}
          className="px-4 py-2.5 border border-border rounded-xl bg-background text-foreground text-sm focus:outline-none focus:ring-2 focus:ring-primary/40"
        >
          <option value="all">All Status</option>
          <option value="succeeded">Paid</option>
          <option value="pending">Pending</option>
          <option value="failed">Failed</option>
        </select>
      </div>
      {/* Transactions Table */}
      <div className="bg-card border border-border rounded-2xl shadow-elevation-1 overflow-hidden">
        {filteredTransactions?.length === 0 ? (
          <div className="text-center py-16">
            <FileText className="w-12 h-12 text-muted-foreground mx-auto mb-4" />
            <h3 className="text-lg font-semibold text-foreground mb-2">No Transactions Found</h3>
            <p className="text-muted-foreground text-sm">No billing history matches your filters.</p>
          </div>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full">
              <thead>
                <tr className="border-b border-border bg-muted/30">
                  <th className="text-left px-6 py-4 text-xs font-semibold text-muted-foreground uppercase tracking-wider">Invoice</th>
                  <th className="text-left px-6 py-4 text-xs font-semibold text-muted-foreground uppercase tracking-wider">Description</th>
                  <th className="text-left px-6 py-4 text-xs font-semibold text-muted-foreground uppercase tracking-wider">Date</th>
                  <th className="text-left px-6 py-4 text-xs font-semibold text-muted-foreground uppercase tracking-wider">Amount</th>
                  <th className="text-left px-6 py-4 text-xs font-semibold text-muted-foreground uppercase tracking-wider">Status</th>
                  <th className="text-right px-6 py-4 text-xs font-semibold text-muted-foreground uppercase tracking-wider">Actions</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-border">
                {filteredTransactions?.map((tx, i) => {
                  const badge = getStatusBadge(tx?.status);
                  const StatusIcon = badge?.icon;
                  return (
                    <tr key={tx?.id || i} className="hover:bg-muted/20 transition-colors">
                      <td className="px-6 py-4">
                        <span className="text-sm font-mono font-medium text-foreground">
                          INV-{(tx?.id || '')?.substring(0, 8)?.toUpperCase()}
                        </span>
                      </td>
                      <td className="px-6 py-4">
                        <p className="text-sm font-medium text-foreground">{tx?.description || tx?.plan_name || 'Subscription Payment'}</p>
                      </td>
                      <td className="px-6 py-4">
                        <div className="flex items-center gap-1.5 text-sm text-muted-foreground">
                          <Calendar className="w-3.5 h-3.5" />
                          {tx?.created_at ? new Date(tx?.created_at)?.toLocaleDateString('en-US', { year: 'numeric', month: 'short', day: 'numeric' }) : 'N/A'}
                        </div>
                      </td>
                      <td className="px-6 py-4">
                        <span className="text-sm font-bold text-foreground">
                          ${(tx?.amount || 0)?.toLocaleString()}
                        </span>
                      </td>
                      <td className="px-6 py-4">
                        <span className={`inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full text-xs font-semibold ${badge?.class}`}>
                          <StatusIcon className="w-3 h-3" />
                          {badge?.label}
                        </span>
                      </td>
                      <td className="px-6 py-4 text-right">
                        <button
                          onClick={() => handleDownloadInvoice(tx)}
                          className="inline-flex items-center gap-1.5 px-3 py-1.5 bg-primary/10 hover:bg-primary/20 text-primary rounded-lg text-xs font-semibold transition-colors"
                        >
                          <Download className="w-3.5 h-3.5" />
                          PDF
                        </button>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        )}
      </div>
      </>
      )}
    </PageLayout>
  );
};

export default BillingHistory;